// components/RequestQuoteDialog.js — форма «Отправить запрос на расчёт в Proxytherm»

import React, { useState } from 'react';
import {
  Dialog, Box, Typography, IconButton, TextField, Button,
  CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.proxythermcad.ru';

const PHONE_RE = /^[\d\s+\-()]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RequestQuoteDialog({ open, onClose, onGetFiles, drawingName }) {
  const [name, setName]       = useState('');
  const [phone, setPhone]     = useState('');
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Сброс формы при закрытии
  const handleClose = () => {
    if (loading) return;
    setName(''); setPhone(''); setEmail('');
    setError(''); setSuccess(false); setFieldErrors({});
    onClose();
  };

  const validateField = (field, value) => {
    if (field === 'name' && !value.trim()) return 'Введите имя';
    if (field === 'phone' && value.trim() && !PHONE_RE.test(value.trim())) return 'Неверный формат телефона';
    if (field === 'email' && value.trim() && !EMAIL_RE.test(value.trim())) return 'Неверный формат email';
    return null;
  };

  const handleFieldChange = (field, value) => {
    if (field === 'name') setName(value);
    if (field === 'phone') setPhone(value);
    if (field === 'email') setEmail(value);
    setError('');
    const err = validateField(field, value);
    setFieldErrors(prev => ({ ...prev, [field]: err }));
  };

  // Валидация всей формы
  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'Введите ваше имя';
    if (phone.trim() && !PHONE_RE.test(phone.trim())) errors.phone = 'Неверный формат телефона';
    if (email.trim() && !EMAIL_RE.test(email.trim())) errors.email = 'Неверный формат email';
    if (!phone.trim() && !email.trim()) return 'Укажите хотя бы телефон или email';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return Object.values(errors)[0];
    }
    return null;
  };

  const handleSend = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');

    try {
      // Получаем файлы от App.js (ptCAD blob + PDF blob)
      const { ptCadBlob, pdfBlob, fileName } = await onGetFiles();

      // Конвертируем blob в base64
      const toBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const ptCadBase64 = ptCadBlob ? await toBase64(ptCadBlob) : null;
      const pdfBase64   = pdfBlob   ? await toBase64(pdfBlob)   : null;

      const now = new Date();
      const dateStr = now.toLocaleDateString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });

      const baseName = fileName || drawingName || 'Чертёж';

      // Отправляем на наш Flask backend
      const response = await fetch(`${BACKEND_URL}/send-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_name:    name.trim(),
          phone:        phone.trim(),
          reply_email:  email.trim(),
          drawing_name: baseName,
          date:         dateStr,
          ptcad_base64: ptCadBase64 || null,
          pdf_base64:   pdfBase64   || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Ошибка сервера: ${response.status}`);
      }

      setSuccess(true);
    } catch (err) {
      console.error('Ошибка отправки запроса:', err);
      setError('Не удалось отправить запрос. Проверьте подключение к интернету и попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          overflow: 'hidden',
          boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)',
        }
      }}
    >
      {/* Шапка */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 2,
        background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff', lineHeight: 1.2 }}>
            Запрос на расчёт в Proxytherm
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
            Чертёж и PDF будут прикреплены к письму автоматически
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={loading}
          sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {success ? (
        /* Экран успеха */
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: '3.5rem', color: '#388e3c', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#212121', mb: 1 }}>
            Запрос успешно отправлен!
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#616161', mb: 3, lineHeight: 1.6 }}>
            Наши специалисты свяжутся с вами в ближайшее время.
            Чертёж и PDF прикреплены к письму.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #b71c1c 0%, #7f0000 100%)' },
              borderRadius: '0.5rem', px: 4, textTransform: 'none', fontWeight: 600,
            }}
          >
            Закрыть
          </Button>
        </Box>
      ) : (
        /* Форма */
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Имя */}
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242', mb: 0.75 }}>
              Ваше имя <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Иван Иванов"
              value={name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              disabled={loading}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name || ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' } }}
            />
          </Box>

          {/* Телефон */}
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242', mb: 0.75 }}>
              Номер телефона
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="+7 (900) 000-00-00"
              value={phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              disabled={loading}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone || ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' } }}
            />
          </Box>

          {/* Email */}
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#424242', mb: 0.75 }}>
              Электронная почта
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="email"
              placeholder="example@mail.ru"
              value={email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email || ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem' } }}
            />
          </Box>

          {/* Инфо о вложениях */}
          <Box sx={{
            p: 1.5, borderRadius: '0.5rem',
            background: '#fff8e1', border: '0.0625rem solid #ffe082',
            display: 'flex', alignItems: 'flex-start', gap: 1,
          }}>
            <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>📎</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#5d4037', lineHeight: 1.5 }}>
              К письму автоматически прикрепятся файлы чертежа <strong>.ptCAD</strong> и <strong>PDF</strong>
            </Typography>
          </Box>

          {/* Ошибка */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: '0.5rem', fontSize: '0.8rem' }}>
              {error}
            </Alert>
          )}

          {/* Кнопки */}
          <Box sx={{ display: 'flex', gap: 1.5, pt: 0.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClose}
              disabled={loading}
              sx={{
                borderRadius: '0.5rem', textTransform: 'none', fontWeight: 600,
                borderColor: '#e0e0e0', color: '#616161',
                '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' },
              }}
            >
              Отмена
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSend}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{
                borderRadius: '0.5rem', textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #b71c1c 0%, #7f0000 100%)' },
                '&.Mui-disabled': { background: '#e0e0e0' },
              }}
            >
              {loading ? 'Отправка...' : 'Отправить запрос'}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
